/**
 * Canonical public route definitions for frontend auth/layout handling.
 *
 * Public auth routes must stay accessible without a session.
 * Intentional bypass routes below are preserved because existing onboarding
 * and manual test flows already depend on them.
 */
export const publicPages = [
  "/",
  "/auth",
  "/login",
  "/register",
  "/forgot-password",
  "/auth/error",
  "/auth/reset-password",
  "/auth/verify-email",
  "/purchase-success",
  "/tradovate/callback",
] as const;

export const publicRoutePrefixes = [
  "/login",
  // Staging password gate (proxy-served before any session exists) — must
  // stay public or the client auth providers would bounce it to /login.
  "/staging-access",
  "/register",
  "/forgot-password",
  "/auth/error",
  "/auth/reset-password",
  "/auth/verify-email",
  "/signup",
  "/onboarding",
  "/tradovate/callback",
  "/test-chart",
  "/test-overlays",
  "/purchase-success",
  "/pod-invite",
  // SV-PORT-001a: New public surfaces for marketing/community/legal.
  // SECURITY NOTE: prefix-matching is inclusive — any nested route under
  // these prefixes (e.g. /docs/admin, /community/staff) silently bypasses
  // middleware auth. If you add an authenticated subroute under one of
  // these prefixes, it MUST gate auth at the page/server-action level OR
  // be added explicitly to config.matcher in middleware.ts.
  "/about",
  // "/affiliates" REMOVED (AFF-4 / SIG-1539): in-app Affiliates HQ is an
  // AppLayout pillar, not a marketing surface. Marketing /affiliates lives
  // on apps/website. A public prefix here would skip auth + AUTH-07.
  "/blog",
  "/docs",
  // "/community" REMOVED (SIG-37 / Codex HIGH-2): it is an in-app AppLayout
  // pillar (SV-45), not a marketing surface — a public prefix here silently
  // bypassed middleware auth + the AUTH-07 entitlement wall for every nested
  // community route. It is now protected via the proxy config.matcher.
  "/legal",
  // Public P&L certificate verification (QR target on shared certs). The
  // page is read-only: it renders the public verify endpoint's response.
  "/verify",
] as const;

export const publicPatterns: RegExp[] = [];

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isPublicRoute(pathname?: string | null): boolean {
  if (!pathname) {
    return false;
  }

  if (publicPages.includes(pathname as (typeof publicPages)[number])) {
    return true;
  }

  if (publicRoutePrefixes.some((prefix) => matchesPrefix(pathname, prefix))) {
    return true;
  }

  return publicPatterns.some((pattern) => pattern.test(pathname));
}

export function buildLoginRedirectPath(redirectTo?: string | null): string {
  if (!redirectTo) {
    return "/login";
  }

  return `/login?redirect=${encodeURIComponent(redirectTo)}`;
}
